import { graphemeLength } from '@up2stay/up2promo-commons';
import { Label } from '@up2stay/up2promo-ui';
import { Switch } from '@up2stay/up2promo-ui';
import type {
  FieldErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';

import { ProfileEditFieldOverrideHint } from '@/core/components/profile/edit/profile-edit-field-override-hint';
import {
  PROFILE_EDIT_MAX_COMPANY,
  PROFILE_EDIT_MAX_JOB_TITLE,
  PROFILE_EDIT_MAX_LOCATION,
  type ProfileFormData,
} from '@/core/components/profile/edit/profile-edit-form-schema';
import {
  SettingsGroup,
  SettingsGroupFooter,
  SettingsGroupLabel,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsInfoHint } from '@/core/components/settings/settings-info-hint';
import { ProfileFieldLockedHint } from '@/core/components/profile-templates/profile-field-locked-hint';
import { InputWithCharCount } from '@/core/components/ui/field-with-char-count';
import type { ProfileTemplateFieldKey } from '@/core/lib/profile-template-fields';

type BasicDict = {
  title: string;
  description: string;
  jobTitle: string;
  jobTitlePlaceholder: string;
  company: string;
  companyPlaceholder: string;
  location: string;
  locationPlaceholder: string;
  showProfileImage: string;
  showProfileImageDescription: string;
};

type FieldOverrideDisplayFn = Parameters<
  typeof ProfileEditFieldOverrideHint
>[0]['getFieldOverrideDisplay'];

type ProfileEditBasicSectionProps = {
  dict: BasicDict;
  formValues: ProfileFormData;
  errors: FieldErrors<ProfileFormData>;
  register: UseFormRegister<ProfileFormData>;
  setValue: UseFormSetValue<ProfileFormData>;
  isLocked: (key: ProfileTemplateFieldKey) => boolean;
  fieldLockedHintMessage: string;
  overrideHintMessage: string;
  resetLabel: string;
  templateValueLabel: string;
  getFieldOverrideDisplay: FieldOverrideDisplayFn;
  onResetField: (key: ProfileTemplateFieldKey) => void;
};

export function ProfileEditBasicSection({
  dict,
  formValues,
  errors,
  register,
  setValue,
  isLocked,
  fieldLockedHintMessage,
  overrideHintMessage,
  resetLabel,
  templateValueLabel,
  getFieldOverrideDisplay,
  onResetField,
}: ProfileEditBasicSectionProps) {
  const watchedJobTitle = formValues.jobTitle || '';
  const watchedCompany = formValues.company || '';
  const watchedLocation = formValues.location || '';

  const overrideHintProps = {
    getFieldOverrideDisplay,
    overrideHintMessage,
    resetLabel,
    templateValueLabel,
    onReset: onResetField,
  };

  return (
    <div className="flex min-w-0 w-full flex-col gap-2">
      <SettingsGroupLabel>{dict.title}</SettingsGroupLabel>
      <SettingsGroup>
        <SettingsGroupRows>
          <SettingsGroupRow>
            <div className="space-y-0.5 min-w-0 w-full">
              <Label htmlFor="jobTitle">{dict.jobTitle}</Label>
              <ProfileFieldLockedHint
                locked={isLocked('jobTitle')}
                message={fieldLockedHintMessage}
              />
              <InputWithCharCount
                id="jobTitle"
                placeholder={dict.jobTitlePlaceholder}
                className="min-w-0 w-full"
                maxLength={PROFILE_EDIT_MAX_JOB_TITLE}
                valueLength={graphemeLength(String(watchedJobTitle ?? ''))}
                disabled={isLocked('jobTitle')}
                {...register('jobTitle')}
              />
              <ProfileEditFieldOverrideHint
                fieldKey="jobTitle"
                {...overrideHintProps}
              />
              {errors.jobTitle ? (
                <p className="text-sm text-destructive">
                  {errors.jobTitle.message}
                </p>
              ) : null}
            </div>
          </SettingsGroupRow>

          <SettingsGroupRow separated>
            <div className="space-y-0.5 min-w-0">
              <Label htmlFor="company">{dict.company}</Label>
              <ProfileFieldLockedHint
                locked={isLocked('company')}
                message={fieldLockedHintMessage}
              />
              <InputWithCharCount
                id="company"
                placeholder={dict.companyPlaceholder}
                className="min-w-0 w-full"
                maxLength={PROFILE_EDIT_MAX_COMPANY}
                valueLength={graphemeLength(String(watchedCompany ?? ''))}
                disabled={isLocked('company')}
                {...register('company')}
              />
              <ProfileEditFieldOverrideHint
                fieldKey="company"
                {...overrideHintProps}
              />
              {errors.company ? (
                <p className="text-sm text-destructive">
                  {errors.company.message}
                </p>
              ) : null}
            </div>
          </SettingsGroupRow>

          <SettingsGroupRow separated>
            <div className="space-y-0.5 min-w-0">
              <Label htmlFor="location">{dict.location}</Label>
              <ProfileFieldLockedHint
                locked={isLocked('location')}
                message={fieldLockedHintMessage}
              />
              <InputWithCharCount
                id="location"
                placeholder={dict.locationPlaceholder}
                className="min-w-0 w-full"
                maxLength={PROFILE_EDIT_MAX_LOCATION}
                valueLength={graphemeLength(String(watchedLocation ?? ''))}
                disabled={isLocked('location')}
                {...register('location')}
              />
              <ProfileEditFieldOverrideHint
                fieldKey="location"
                {...overrideHintProps}
              />
              {errors.location ? (
                <p className="text-sm text-destructive">
                  {errors.location.message}
                </p>
              ) : null}
            </div>
          </SettingsGroupRow>

          <SettingsGroupRow separated>
            <div className="space-y-1.5 min-w-0">
              <div className="flex items-center justify-between gap-4">
                <div className="flex min-w-0 flex-1 items-center gap-0.5">
                  <Label htmlFor="showProfileImage">
                    {dict.showProfileImage}
                  </Label>
                  <SettingsInfoHint label={dict.showProfileImage}>
                    {dict.showProfileImageDescription}
                  </SettingsInfoHint>
                </div>
                <Switch
                  id="showProfileImage"
                  checked={formValues.showProfileImage ?? true}
                  disabled={isLocked('showProfileImage')}
                  onCheckedChange={(checked) =>
                    setValue('showProfileImage', checked)
                  }
                />
              </div>
              <ProfileFieldLockedHint
                locked={isLocked('showProfileImage')}
                message={fieldLockedHintMessage}
              />
              <ProfileEditFieldOverrideHint
                fieldKey="showProfileImage"
                {...overrideHintProps}
              />
            </div>
          </SettingsGroupRow>
        </SettingsGroupRows>
      </SettingsGroup>
      <SettingsGroupFooter>{dict.description}</SettingsGroupFooter>
    </div>
  );
}
